import React, { useState } from 'react';
import {
  Typography,
  Button,
  Input,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { postBulkCreateProductGroups } from '../../../requests/api/product-groups';
import useStyles from './styles';

const ProductGroupsBulkUploadView = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && !selectedFile.name.toLowerCase().endsWith('.csv')) {
      setSnackbar({ open: true, message: t('productGroup.onlyCSV'), severity: 'error' });
      return;
    }
    setFile(selectedFile);
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter((line) => line.trim());
    const headers = lines[0].split(',').map((h) => h.trim());

    return lines.slice(1).map((line) => {
      const values = line.split(',').map((v) => v.trim());
      const obj = {};
      headers.forEach((header, index) => {
        if (header === 'product_ids') {
          obj[header] = values[index] && values[index] !== ''
            ? values[index].split(';').map((id) => parseInt(id.trim(), 10)).filter((id) => !Number.isNaN(id))
            : [];
        } else if (header === 'company_id') {
          obj[header] = parseInt(values[index], 10);
        } else {
          obj[header] = values[index];
        }
      });
      return obj;
    });
  };

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);
    try {
      const text = await file.text();
      const productGroups = parseCSV(text);
      const result = await postBulkCreateProductGroups(productGroups);

      if (result.success) {
        setSnackbar({ open: true, message: t('common.savedSuccessfully'), severity: 'success' });
        setFile(null);
      } else {
        setSnackbar({ open: true, message: t('common.savedUnsuccessfully'), severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('common.savedUnsuccessfully'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <Typography variant='h4' className={styles.title}>{t('productGroup.title')}</Typography>
      </div >
      <div>
        <div class="upload-instructions">
          <p>
            Sube un archivo CSV que contenga una fila de encabezados y las siguientes columnas
            obligatorias, en este orden:
          </p>
          <ul>
            <li>
              <code>company_id</code> — Identificador numérico de la empresa (único por fila).
            </li>
            <li><code>name</code> — Nombre corto del grupo.</li>
            <li>
              <code>description</code> — Descripción del grupo
              (puede contener espacios y caracteres especiales).
            </li>
            <li>
              <code>product_ids</code> — Lista de IDs de producto asociados, separados por punto
              y comas (;).
            </li>
          </ul>
          <p>Ejemplo: </p>
          <table border="1" cellspacing="0" cellpadding="6">
            <thead>
              <tr>
                <th>company_id</th>
                <th>name</th>
                <th>description</th>
                <th>product_ids</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>GDM</td>
                <td>Grupo de Mariscos</td>
                <td>1;2;3</td>
              </tr>
              <tr>
                <td>2</td>
                <td>GDF</td>
                <td>Grupo de Frutas</td>
                <td>10;11</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: '20px' }}>
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ marginRight: '10px' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!file || loading}
          >
            {loading ? t('loading') : t('common.uploadFile')}
          </Button>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductGroupsBulkUploadView;
